/**
 * @file TransferIntegrationDialog.tsx
 * @description «Перенести в магазин…» общей интеграции вида «только своя»
 * со страницы «Общие интеграции» (phase19.28 этап А; uiux-platform-shops
 * §6 «полоса переноса»; PL13, раздел 5 platform.md — личные настройки
 * Chaiberi переносятся в магазин до второго магазина).
 *
 * Магазин выбирается поиском из `GET /admin/platform/shops`; отказы —
 * 409 «у магазина уже есть своя» и 422 «настройка не общая» — текстом в окне.
 */

import { useState } from "react";

import { usePlatformResource } from "../../../hooks/platform/usePlatformResource";
import { ApiError } from "../../../lib/api-client";
import { INTEGRATION_KIND_META, type PlatformIntegrationDto } from "../../../lib/api-platform-integrations";
import { platformShops } from "../../../lib/api-platform-shops";
import { apiErrorText } from "../../../lib/platform/shop-errors";
import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import { Combobox } from "../../ui/combobox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Label } from "../../ui/label";
import { useNotify } from "../../ui/NotifyProvider";

const TRANSFER_CODES: Readonly<Record<string, string>> = {
  integration_shop_has_kind: "У этого магазина уже есть своя настройка этого вида — выберите другой магазин",
  integration_not_shared: "Настройка уже принадлежит магазину — переносить нечего",
};

interface Props {
  item: PlatformIntegrationDto | null;
  onClose: () => void;
  onTransferred: () => void;
}

export function TransferIntegrationDialog({ item, onClose, onTransferred }: Props) {
  const notify = useNotify();
  const [shopId, setShopId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const shops = usePlatformResource(
    item ? "shops?q=" : null,
    () => platformShops.list(),
    "Не удалось загрузить магазины",
  );

  function close() {
    setShopId(null);
    setError(null);
    onClose();
  }

  async function submit() {
    if (!item) return;
    if (!shopId) return setError("Выберите магазин");
    setBusy(true);
    setError(null);
    try {
      await platformShops.transferIntegration(item.id, shopId);
      notify.success(`«${item.name}» перенесена в магазин`);
      setShopId(null);
      onTransferred();
    } catch (err) {
      const known = err instanceof ApiError ? TRANSFER_CODES[err.code] : undefined;
      setError(known ?? apiErrorText(err, "Не удалось перенести настройку"));
    } finally {
      setBusy(false);
    }
  }

  const kindLabel = item ? INTEGRATION_KIND_META[item.kind]?.label ?? item.kind : "";
  return (
    <Dialog open={item !== null} onOpenChange={(open) => !open && !busy && close()}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Перенести «{item?.name}» в магазин</DialogTitle>
          <DialogDescription>
            {kindLabel}: настройка станет своей у выбранного магазина и перестанет быть общей платформы.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="transfer-shop">Магазин</Label>
          <Combobox
            id="transfer-shop"
            options={(shops.data ?? []).map((shop) => ({ value: shop.id, label: shop.displayName, description: shop.slug }))}
            value={shopId}
            onChange={setShopId}
            placeholder={shops.loading ? "Загружаем магазины…" : "Выберите магазин"}
            searchPlaceholder="Название или slug"
            emptyText="Такого магазина нет"
            disabled={shops.loading}
          />
        </div>
        {shops.error ? <Alert severity="error">{shops.error}</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={() => void submit()} loading={busy}>
            Перенести
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
